import Big from 'big.js';
import {combineLatest} from 'rxjs';
import {distinctUntilChanged, filter, map, share} from 'rxjs/operators';
import {DEFAULT_MONEY_PRECISION, DEFAULT_USD_PRICE_SCALE} from '@/const/data-config';
import {numUtil} from '@/helper/num';
import {fiat$} from '@/state/fiat';
import {currenciesCache$} from '@/config';
import {indexPricesCache$} from '@/price-index/price-index';
import type {AssetDigital, CalcMoneyData, CalcPriceWU, CurrencyEntity, PricesCache} from '@/types';

const fiatRate$ = combineLatest([indexPricesCache$, fiat$]).pipe(
  filter(([indexPricesCache, fiat]: [PricesCache, string]) => indexPricesCache.has('.' + fiat)),
  distinctUntilChanged((p, c) => p[0].get('.' + c) !== c[0].get('.' + c))
);

export const calcPriceWU$ = fiatRate$.pipe(map(convertCalcPriceWU), share());
export const calcMoneyData$ = combineLatest([currenciesCache$, fiatRate$]).pipe(map(convertCalcMoneyData), share());

function convertCalcPriceWU([indexPricesCache, fiat]: [PricesCache, string]): CalcPriceWU {
  const fiatRate = indexPricesCache.get('.' + fiat);
  return function (priceEp: number, priceScale: number, quoteCurrency: string): string {
    const scale = priceScale - DEFAULT_USD_PRICE_SCALE;
    const quoteRate = indexPricesCache.get('.' + quoteCurrency);
    if (!quoteRate) {
      return '--' + fiat;
    }
    const money = Number(Big(priceEp).times(quoteRate.priceEp).div(fiatRate.priceEp).div(quoteRate.priceFactor));
    const precision = money < 1e2 ? 6 : DEFAULT_MONEY_PRECISION;
    return numUtil.delimit(numUtil.floor(money, precision, scale)) + ' ' + fiat;
  };
}

function convertCalcMoneyData([currenciesCache, [indexPricesCache, fiat]]: [
  Map<string, CurrencyEntity>,
  [PricesCache, string]
]): CalcMoneyData {
  const fiatInfo = indexPricesCache.get('.' + fiat);
  return function (coinCurrency: string, amountEv: AssetDigital, amountEvUnScale: boolean = false): [string, string] {
    const money = convertCalcMoney(
      amountEv,
      coinCurrency,
      currenciesCache,
      indexPricesCache,
      fiatInfo.priceEp,
      amountEvUnScale
    );
    return [numUtil.delimit(money) + ' ' + fiat, money]; // [moneyWU, money]
  };
}

function convertCalcMoney(
  amountEv: AssetDigital,
  coinCurrency: string,
  currenciesCache: Map<string, CurrencyEntity>,
  indexPricesCache,
  fiatInfoPriceEp,
  amountEvUnScale = false
) {
  const currencyInfo = currenciesCache.get(coinCurrency);
  if (!currencyInfo) {
    return (0).toFixed(DEFAULT_MONEY_PRECISION);
  }
  const coinIndexPrice = indexPricesCache.get('.' + coinCurrency);
  if (!coinIndexPrice) {
    return (0).toFixed(DEFAULT_MONEY_PRECISION);
  }
  const moneyEv = calcMoneyEv(amountEv, coinIndexPrice.priceEp, fiatInfoPriceEp);
  const scale = amountEvUnScale ? 0 : currencyInfo.valueScale;
  return numUtil.floor(moneyEv, DEFAULT_MONEY_PRECISION, scale);
}

function calcMoneyEv(amountEv: AssetDigital, priceEp: number, fiatPriceEp: number): string {
  return Big(amountEv).times(priceEp).div(fiatPriceEp).toString();
}
